/**
 * Briques partagées par les cartes du formulaire : erreurs par élément,
 * boutons d'ordre / suppression, champ numérique.
 */

import { createContext, useContext, useId } from "react";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { Button, Input, Label } from "@repo/ui";
import { cn } from "@repo/utils";

/** Erreurs de `validerScorecard` par chemin (`competences[0].questions[1]`). */
export const ProblemesContext = createContext<Map<string, string[]>>(new Map());

/** Messages d'erreur rattachés exactement à cet élément. */
export function MessagesProbleme({ chemin }: { chemin: string }) {
  const messages = useContext(ProblemesContext).get(chemin);
  if (!messages?.length) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-sm text-destructive" role="alert">
      {messages.map((m) => (
        <li key={m}>{m}</li>
      ))}
    </ul>
  );
}

export function aDesProblemes(
  problemes: Map<string, string[]>,
  chemin: string,
): boolean {
  return (problemes.get(chemin)?.length ?? 0) > 0;
}

/** Monter / descendre / supprimer : pas de glisser-déposer (bundle léger). */
export function ActionsElement({
  libelle,
  index,
  total,
  onDeplacer,
  onSupprimer,
}: {
  /** « la question 3 » : nom accessible des boutons. */
  libelle: string;
  index: number;
  total: number;
  onDeplacer: (sens: -1 | 1) => void;
  onSupprimer: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Monter ${libelle}`}
        disabled={index === 0}
        onClick={() => onDeplacer(-1)}
      >
        <ArrowUp />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Descendre ${libelle}`}
        disabled={index === total - 1}
        onClick={() => onDeplacer(1)}
      >
        <ArrowDown />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Supprimer ${libelle}`}
        onClick={onSupprimer}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

/**
 * Champ numérique : un champ vidé vaut `NaN`, que `validerScorecard` signale,
 * plutôt qu'un 0 silencieux qui passerait pour une saisie.
 */
export function ChampNombre({
  label,
  valeur,
  onChange,
  pas,
  invalide,
  className,
}: {
  label: string;
  valeur: number;
  onChange: (valeur: number) => void;
  pas: number;
  invalide?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <Label htmlFor={id} className="mb-1 text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        step={pas}
        value={Number.isNaN(valeur) ? "" : valeur}
        aria-invalid={invalide || undefined}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
    </div>
  );
}

export function ChampTexte({
  label,
  valeur,
  onChange,
  invalide,
  placeholder,
  className,
  classeLibelle,
  classeChamp,
}: {
  label: string;
  valeur: string;
  onChange: (valeur: string) => void;
  invalide?: boolean;
  placeholder?: string;
  className?: string;
  /** Remplace le style du libellé (niveau de hiérarchie). */
  classeLibelle?: string;
  classeChamp?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <Label
        htmlFor={id}
        className={cn("mb-1 text-xs text-muted-foreground", classeLibelle)}
      >
        {label}
      </Label>
      <Input
        id={id}
        value={valeur}
        placeholder={placeholder}
        className={classeChamp}
        aria-invalid={invalide || undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
